import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquare, Send, Paperclip, X, Loader2, ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_PHOTOS = 3;
const MAX_BYTES = 3 * 1024 * 1024; // 3 MB per photo

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const s = String(reader.result || "");
      const idx = s.indexOf(",");
      resolve(idx >= 0 ? s.slice(idx + 1) : s);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * MessageBoard — per-job chat between homeowner and pro.
 * Backend endpoints:
 *  GET  /api/jobs/{jobId}/messages                (participants only, idempotent)
 *  POST /api/jobs/{jobId}/messages                (participants only)
 *  POST /api/jobs/{jobId}/messages/read           (mark all as read for viewer)
 *  GET  /api/jobs/{jobId}/messages/{mid}/photos/{pid}   (streams bytes)
 *
 * Props:
 *  - jobId: string
 *  - viewerRole: 'homeowner' | 'contractor'
 *  - authHeader?: { Authorization: 'Bearer ...' }
 *  - readOnly?: boolean (for admin view)
 *  - initialMessages?: [] (for admin, we prefetch)
 *  - jobStatus?: string
 *  - onRead?: () => void  (callback fired after the server marks messages read,
 *                          use to refresh an UnreadBadge)
 */
export default function MessageBoard({
  jobId,
  viewerRole = "homeowner",
  authHeader,
  readOnly = false,
  initialMessages,
  jobStatus,
  onRead,
}) {
  const [messages, setMessages] = useState(initialMessages || []);
  const [loading, setLoading] = useState(!initialMessages);
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]); // [{file, previewUrl}]
  const [sending, setSending] = useState(false);
  const scrollerRef = useRef(null);
  const fileInputRef = useRef(null);

  const canSend =
    !readOnly &&
    (!jobStatus || ["accepted", "in_progress", "completed"].includes(jobStatus));

  const load = useCallback(async () => {
    if (readOnly) return; // admin gets messages via parent
    try {
      const { data } = await axios.get(`${API}/jobs/${jobId}/messages`, {
        withCredentials: true,
        headers: authHeader || undefined,
      });
      setMessages(data);
    } catch (err) {
      // 403 = not a participant; 404 = job missing — surface gracefully
      if (err?.response?.status !== 403) {
        // Silent — chat will simply appear empty
      }
    } finally {
      setLoading(false);
    }
  }, [jobId, authHeader, readOnly]);

  // Explicit read-receipt. Debounced so the UnreadBadge has time to render
  // its first count before we clear it.
  const markRead = useCallback(async () => {
    if (readOnly) return;
    try {
      await axios.post(
        `${API}/jobs/${jobId}/messages/read`,
        {},
        { withCredentials: true, headers: authHeader || undefined }
      );
      if (typeof onRead === "function") onRead();
    } catch {
      /* silent */
    }
  }, [jobId, authHeader, readOnly, onRead]);

  useEffect(() => {
    if (initialMessages) {
      setMessages(initialMessages);
      setLoading(false);
      return;
    }
    load();
    if (readOnly) return;
    // Give the UnreadBadge (~500ms fetch) time to paint before we mark as read.
    const readT = setTimeout(() => markRead(), 1800);
    const t = setInterval(() => {
      load();
      markRead();
    }, 8000);
    return () => {
      clearTimeout(readT);
      clearInterval(t);
    };
  }, [load, markRead, initialMessages, readOnly]);

  useEffect(() => {
    // Auto-scroll on new messages
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages.length]);

  const onPick = () => fileInputRef.current?.click();

  const onFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const remaining = MAX_PHOTOS - files.length;
    if (picked.length > remaining) {
      toast.error(`Max ${MAX_PHOTOS} photos per message`);
    }
    const accepted = [];
    for (const f of picked.slice(0, remaining)) {
      if (!f.type.startsWith("image/")) {
        toast.error(`${f.name}: only image files`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name}: max 3 MB`);
        continue;
      }
      accepted.push({ file: f, previewUrl: URL.createObjectURL(f) });
    }
    setFiles((prev) => [...prev, ...accepted]);
    e.target.value = "";
  };

  const removeFile = (i) => {
    setFiles((prev) => {
      const copy = [...prev];
      URL.revokeObjectURL(copy[i].previewUrl);
      copy.splice(i, 1);
      return copy;
    });
  };

  const send = async () => {
    if (!body.trim() && files.length === 0) {
      toast.error("Type something or attach a photo");
      return;
    }
    setSending(true);
    try {
      const photos = [];
      for (const { file } of files) {
        const b64 = await readFileAsBase64(file);
        photos.push({
          filename: file.name,
          mime_type: file.type,
          content_base64: b64,
        });
      }
      const { data } = await axios.post(
        `${API}/jobs/${jobId}/messages`,
        { body: body.trim(), photos },
        { withCredentials: true, headers: authHeader || undefined }
      );
      setMessages((prev) => [...prev, data]);
      setBody("");
      files.forEach((f) => URL.revokeObjectURL(f.previewUrl));
      setFiles([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send");
    } finally {
      setSending(false);
    }
  };

  const photoUrl = (msgId, photoId) =>
    `${API}/jobs/${jobId}/messages/${msgId}/photos/${photoId}`;

  return (
    <div
      className="border border-[#C9C6C6]/10 bg-[#0D1421]"
      data-testid={`message-board-${jobId}`}
    >
      <div className="px-4 py-3 border-b border-[#C9C6C6]/10 flex items-center gap-2">
        <MessageSquare className="w-4 h-4 text-[#0E93EC]" />
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#EEEEEF]">
          Message board
        </div>
        <div className="text-[11px] text-[#C9C6C6] ml-auto">
          {readOnly ? "Admin · read-only" : "Direct chat with your " + (viewerRole === "homeowner" ? "pro" : "homeowner")}
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="max-h-[420px] min-h-[180px] overflow-y-auto px-4 py-4 space-y-4 bg-[#010204]"
        data-testid={`message-list-${jobId}`}
      >
        {loading ? (
          <div className="flex items-center gap-2 text-[#C9C6C6] text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading messages…
          </div>
        ) : messages.length === 0 ? (
          <div className="text-sm text-[#C9C6C6]">
            No messages yet.{" "}
            {readOnly
              ? "The homeowner and pro have not chatted on this job."
              : "Say hi to kick things off."}
          </div>
        ) : (
          messages.map((m) => {
            const mine = !readOnly && m.sender_role === viewerRole;
            return (
              <div
                key={m.id}
                className={`flex ${mine ? "justify-end" : "justify-start"}`}
                data-testid={`message-${m.id}`}
              >
                <div className={`max-w-[80%] ${mine ? "items-end text-right" : ""}`}>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1">
                    {m.sender_name}
                    <span className="text-[#C9C6C6]"> · </span>
                    <span className={m.sender_role === "homeowner" ? "text-[#0E93EC]" : "text-[#0E93EC]"}>
                      {m.sender_role === "homeowner" ? "Homeowner" : "Pro"}
                    </span>
                    <span className="text-[#C9C6C6]"> · </span>
                    {timeAgo(m.created_at)}
                  </div>
                  <div
                    className={`inline-block px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap border ${
                      mine
                        ? "bg-[#0E93EC] text-[#EEEEEF] border-[#0E93EC]"
                        : "bg-[#0D1421] text-[#EEEEEF] border-[#C9C6C6]/10"
                    }`}
                  >
                    {m.body || <span className="italic opacity-70">(photos)</span>}
                  </div>
                  {m.photos && m.photos.length > 0 && (
                    <div className={`mt-2 flex flex-wrap gap-2 ${mine ? "justify-end" : ""}`}>
                      {m.photos.map((p) => (
                        <a
                          key={p.id}
                          href={photoUrl(m.id, p.id)}
                          target="_blank"
                          rel="noreferrer"
                          className="block w-20 h-20 border border-[#C9C6C6]/10 bg-[#010204] overflow-hidden"
                          data-testid={`message-photo-${p.id}`}
                        >
                          <img
                            src={photoUrl(m.id, p.id)}
                            alt={p.filename}
                            className="w-full h-full object-cover"
                          />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {!readOnly && (
        <div className="px-4 py-3 border-t border-[#C9C6C6]/10 bg-[#0D1421]">
          {!canSend ? (
            <div className="text-xs text-[#C9C6C6] italic">
              The message board opens once the estimate is accepted.
            </div>
          ) : (
            <>
              {files.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {files.map((f, i) => (
                    <div
                      key={i}
                      className="relative w-16 h-16 border border-[#C9C6C6]/10 bg-[#010204] overflow-hidden"
                    >
                      <img
                        src={f.previewUrl}
                        alt={f.file.name}
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() => removeFile(i)}
                        className="absolute -top-1 -right-1 bg-[#010204] text-white w-5 h-5 flex items-center justify-center"
                        data-testid={`message-photo-remove-${i}`}
                        aria-label="Remove photo"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2 items-end">
                <Textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  placeholder="Write a message… (⌘/Ctrl + Enter to send)"
                  rows={2}
                  className="flex-1 bg-[#010204] border-[#C9C6C6]/10 rounded-none focus-visible:ring-2 focus-visible:ring-[#0E93EC] focus-visible:ring-offset-0"
                  data-testid={`message-input-${jobId}`}
                />
                <div className="flex flex-col gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={onFiles}
                    data-testid={`message-file-input-${jobId}`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={onPick}
                    disabled={files.length >= MAX_PHOTOS}
                    className="rounded-none h-10 w-10 p-0 border border-[#C9C6C6]/10 hover:bg-[#EEEEEF]/10"
                    title={`Attach photos (max ${MAX_PHOTOS})`}
                    data-testid={`message-attach-${jobId}`}
                  >
                    <Paperclip className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={send}
                    disabled={sending || (!body.trim() && files.length === 0)}
                    className="rounded-none bg-[#0E93EC] hover:bg-[#0E2F5F] text-white h-10 w-10 p-0"
                    data-testid={`message-send-${jobId}`}
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
              <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-[#C9C6C6] flex items-center gap-1">
                <ImageIcon className="w-3 h-3" /> Max {MAX_PHOTOS} photos per message · 3 MB each
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
